import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { FiX, FiCheck, FiLock, FiZap, FiAlertCircle } from "react-icons/fi";
import { fetchPlanState, fetchPlans, changePlan, openRazorpayCheckout } from "../lib/billingApi";
import { planLabel } from "../utils/tier";

const FEATURE_LABELS = {
    custom_domain: "Custom domain",
    white_label: "White-label branding",
    sso: "Single Sign-On (SSO)",
    agent_actions: "Agent Actions",
    agent_actions_unlimited: "Unlimited Agent Actions",
    response_intelligence_advanced: "Advanced Response Intelligence",
    response_intelligence_basic: "Response Intelligence",
    conditional_logic: "Conditional logic",
    conversational_mode: "Conversational forms",
    remove_branding: "Remove ContentFlashAI branding",
    api_access: "API access",
    team_seats: "Team seats",
    audit_logs: "Audit logs",
    multi_workspace: "Multiple workspaces",
    custom_thank_you_pages: "Custom thank-you pages",
    enrichment_steps: "Enrichment steps",
    human_approval: "Human approval steps",
    voice_video_answers: "Voice & video answers",
    agency_dashboard: "Agency dashboard",
    byok_available: "Bring your own OpenAI key",
};
const featLabel = (k) => FEATURE_LABELS[k] || "this feature";

function priceStr(plan, currency, cycle) {
    if (!plan) return "";
    if (currency === "INR") {
        const paise = cycle === "yearly" ? plan.pricing.inr.yearlyPaise : plan.pricing.inr.monthlyPaise;
        return `₹${(paise / 100).toLocaleString("en-IN")}`;
    }
    const cents = cycle === "yearly" ? plan.pricing.usd.yearlyCents : plan.pricing.usd.monthlyCents;
    return `$${(cents / 100).toFixed(0)}`;
}

const UpgradeModalContext = createContext({ openUpgradeModal: () => {} });
export const useUpgradeModal = () => useContext(UpgradeModalContext);

export function UpgradeModalProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [ctx, setCtx] = useState(null);
    const [phase, setPhase] = useState("loading"); // loading | idle | processing | success | error
    const [error, setError] = useState(null);
    const [planState, setPlanState] = useState(null);
    const [plans, setPlans] = useState([]);
    const [cycle, setCycle] = useState("yearly");
    const [target, setTarget] = useState(null);
    const plansRef = useRef([]);

    const openUpgradeModal = useCallback(async (context = {}) => {
        setCtx(context);
        setOpen(true);
        setPhase("loading");
        setError(null);
        try {
            const [ps, pl] = await Promise.all([
                fetchPlanState(),
                plansRef.current.length ? Promise.resolve({ plans: plansRef.current }) : fetchPlans(),
            ]);
            if (pl.plans) { plansRef.current = pl.plans; setPlans(pl.plans); }
            setPlanState(ps);
            setCycle("yearly");
            // resolve recommended/target plan
            let rec = context.recommendedPlan;
            if (!rec && context.requestedFeature) {
                rec = (ps.upgrade_options || []).find((slug) => {
                    const p = plansRef.current.find((x) => x.slug === slug);
                    return p && p.features[context.requestedFeature];
                });
            }
            rec = rec || (ps.upgrade_options || [])[0] || "pro";
            setTarget(rec);
            setPhase("idle");
        } catch (e) {
            setError(e.message);
            setPhase("error");
        }
    }, []);

    const close = useCallback(() => {
        setOpen(false);
        if (phase === "success") window.dispatchEvent(new Event("cfai:plan-changed"));
        setTimeout(() => { setCtx(null); setPlanState(null); setPhase("loading"); setError(null); }, 200);
    }, [phase]);

    const dismissable = ctx ? (ctx.dismissable ?? ctx.trigger !== "feature_gate") : true;

    // ESC support (only when dismissable)
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape" && dismissable && phase !== "processing") close(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, dismissable, phase, close]);

    const targetPlan = plans.find((p) => p.slug === target);
    const ltd = !!planState?.has_ltd;
    const isLowTier = planState && ["free", "starter", "pro"].includes(planState.effective_plan);
    const fLabel = ctx?.requestedFeature ? featLabel(ctx.requestedFeature) : null;

    let header = "Upgrade your plan";
    let subtext = "Unlock more of ContentFlashAI.";
    let ctaLabel = "Upgrade now";
    if (ltd) {
        header = "Add this to your lifetime access";
        subtext = "Your AppSumo lifetime tier stays active — this adds features beyond what's included.";
        ctaLabel = "Add to my account";
    } else if (fLabel && ctx?.requestedFeature && isLowTier) {
        header = `Unlock ${fLabel}`;
        subtext = "Upgrade your plan to access this and more.";
    } else if (target) {
        header = `Upgrade to ${planLabel(target)}`;
        subtext = "This feature requires a higher plan.";
    }

    const handleUpgrade = async () => {
        if (!target) return;
        setPhase("processing");
        setError(null);
        try {
            const result = await changePlan({
                targetPlanSlug: target,
                billingCycle: cycle,
                currency: (planState?.currency || "INR").toLowerCase(),
            });
            if (result.scheduledAt && !result.subscriptionId) {
                // Downgrade-style (no immediate payment) — treat as success.
                setPhase("success");
                return;
            }
            await openRazorpayCheckout(
                { subscriptionId: result.subscriptionId, shortUrl: result.shortUrl, keyId: result.keyId, planName: planLabel(target) },
                { onSuccess: () => setPhase("success"), onDismiss: () => setPhase("idle") }
            );
        } catch (e) {
            setError(e.message);
            setPhase("error");
        }
    };

    return (
        <UpgradeModalContext.Provider value={{ openUpgradeModal }}>
            {children}
            {open && (
                <div
                    className="fixed inset-0 z-[100] flex justify-end sm:items-stretch items-end bg-black/40 backdrop-blur-sm font-Rajdhani"
                    onClick={() => { if (dismissable && phase !== "processing") close(); }}
                >
                    <div
                        className="bg-white w-full sm:max-w-md h-auto sm:h-full sm:rounded-none rounded-t-2xl shadow-2xl p-7 overflow-y-auto animate-[slideIn_.2s_ease-out]"
                        style={{ animationName: "none" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close */}
                        {dismissable && phase !== "processing" && (
                            <button onClick={close} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                                <FiX className="w-5 h-5" />
                            </button>
                        )}

                        {phase === "loading" && (
                            <div className="flex items-center justify-center h-64">
                                <div className="w-8 h-8 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin" />
                            </div>
                        )}

                        {phase === "error" && (
                            <div className="text-center py-10">
                                <FiAlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                                <h3 className="text-lg font-bold text-gray-900">Something went wrong</h3>
                                <p className="text-sm text-gray-500 mt-1">{error}</p>
                                <button onClick={() => setPhase("idle")} className="mt-5 px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">Try again</button>
                            </div>
                        )}

                        {phase === "success" && (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center animate-[pop_.3s_ease-out]">
                                    <FiCheck className="w-9 h-9 text-green-600" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">You're all set!</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {planLabel(target)} is now active{fLabel ? ` — ${fLabel} is unlocked.` : "."}
                                </p>
                                <button onClick={close} className="mt-6 px-6 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">Done</button>
                            </div>
                        )}

                        {(phase === "idle" || phase === "processing") && planState && (
                            <div>
                                <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-4">
                                    {ltd ? <FiZap className="w-5 h-5" /> : <FiLock className="w-5 h-5" />}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">{header}</h3>
                                <p className="text-sm text-gray-500 mt-1.5">{subtext}</p>

                                {/* LTD feature-gate clarity */}
                                {ltd && ctx?.trigger === "feature_gate" && (
                                    <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                        These features aren't included in your lifetime deal — they require an active subscription.
                                    </p>
                                )}

                                {/* Locked feature chip */}
                                {fLabel && (
                                    <div className="mt-4 flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                                        <FiLock className="w-4 h-4 text-gray-400" /> <span className="font-medium">{fLabel}</span>
                                        <span className="ml-auto text-sm text-gray-600 dark:text-gray-400">on {planLabel(target)}+</span>
                                    </div>
                                )}

                                {/* Recommended plan card */}
                                {targetPlan && (
                                    <div className="mt-5 rounded-xl border-2 border-indigo-200 bg-indigo-50/40 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="font-bold text-gray-900">{targetPlan.name}</span>
                                            <span className="text-right">
                                                <span className="text-xl font-bold text-gray-900">{priceStr(targetPlan, planState.currency, cycle)}</span>
                                                <span className="text-xs text-gray-500">/{cycle === "yearly" ? "yr" : "mo"}</span>
                                            </span>
                                        </div>
                                        {/* billing cycle toggle */}
                                        <div className="mt-3 inline-flex p-0.5 bg-white border border-gray-200 rounded-lg text-xs font-medium">
                                            <button onClick={() => setCycle("yearly")} className={`px-3 py-1 rounded-md ${cycle === "yearly" ? "bg-indigo-600 text-white" : "text-gray-500"}`}>Annual · save 17%</button>
                                            <button onClick={() => setCycle("monthly")} className={`px-3 py-1 rounded-md ${cycle === "monthly" ? "bg-indigo-600 text-white" : "text-gray-500"}`}>Monthly</button>
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={handleUpgrade}
                                    disabled={phase === "processing" || !target}
                                    className="mt-5 w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition disabled:opacity-60 flex items-center justify-center gap-2"
                                >
                                    {phase === "processing" ? (<><span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Processing…</>) : ctaLabel}
                                </button>

                                <a href="/settings/plans" className="block text-center text-sm text-indigo-600 hover:text-indigo-700 mt-3 no-underline">Compare all plans</a>

                                {ltd && (
                                    <p className="mt-4 text-xs text-center text-gray-400">Your lifetime tier and your subscription work together. Cancel anytime.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </UpgradeModalContext.Provider>
    );
}
